import { computed, Injectable, Signal } from '@angular/core';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';

/**
 * Durable marker written at the single PSP-redirect point
 * (`CheckoutStore.persistOrderBeforeRedirect`) so that a return to `/{token}`
 * **without** query params — browser back button or direct URL — can be
 * recognized as "came from a payment redirect" and offered seat release, the
 * same safeguard the PSP's own "return to store" button already provides.
 *
 * Design (see issue #1180 plan):
 * - **Trigger, not authority.** The marker only signals that a redirect
 *   happened for a token; the decision to release is the *conjunction* with
 *   `order.status === 'cart'` read from the `httpResource` (never from here).
 *   `status !== 'cart'` clears the marker and shows nothing — that protects
 *   successful/pending/offline returns without per-provider exceptions.
 * - **Scoped by `order.token`.** A new order from fan-portal carries a new
 *   token, so a stale marker is inert by construction — no cleanup query param
 *   needed.
 * - **Provider-agnostic.** Written at the one redirect site, so future redirect
 *   providers (Fintoc, Digitalfemsa) are covered without per-provider writes.
 * - **Graceful degradation (AC7).** `injectLocalStorage` wraps every storage
 *   access in try/catch; with `localStorage` blocked (Safari private / ITP) the
 *   marker simply won't persist across the redirect, so the flow degrades to
 *   the current behavior and never breaks.
 */
export interface PspRedirectMarker {
  orderToken: string;
  redirectUrl: string;
  provider: string;
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class PspRedirectMarkerService {
  readonly #STORAGE_KEY = 'stx_psp_redirect';
  readonly #MAX_AGE_MINUTES = 15;

  readonly #storedMarker = injectLocalStorage<PspRedirectMarker | null>(
    this.#STORAGE_KEY,
    { defaultValue: null },
  );

  /**
   * The current marker, or `null` if none / expired / malformed.
   * A marker older than {@link #MAX_AGE_MINUTES} is treated as expired so a
   * marker left from a session whose server hold already expired cannot
   * trigger a stale release prompt (edge case 10). The expired entry is inert
   * (read as `null`) and is overwritten by the next `setMarker` or cleared by
   * the checkout exit paths — kept pure here because writing to a signal
   * inside a `computed` is forbidden.
   */
  public readonly marker: Signal<PspRedirectMarker | null> = computed(() => {
    const stored = this.#storedMarker();
    if (!stored || this.#isExpired(stored)) {
      return null;
    }
    return stored;
  });

  /**
   * Whether a live marker exists for the given order token. Reads the
   * {@link marker} signal, so it is reactive inside a `computed`/`effect`.
   */
  public hasMarker(orderToken: string): boolean {
    const stored = this.marker();
    return stored?.orderToken === orderToken;
  }

  /**
   * The PSP redirect URL recorded for the given token, or `null` if no live
   * marker matches. Used to re-open the payment session when the user chooses
   * "continue with my payment".
   */
  public getRedirectUrl(orderToken: string): string | null {
    const stored = this.marker();
    if (stored?.orderToken !== orderToken) {
      return null;
    }
    return stored.redirectUrl;
  }

  /**
   * Record a PSP redirect for `orderToken`. Writes durably so the marker
   * survives the `location.href` to the PSP (unlike the in-memory
   * `pendingExternalRedirect` flag, which dies on that navigation).
   */
  public setMarker(input: {
    orderToken: string;
    redirectUrl: string;
    provider: string;
  }): void {
    const marker: PspRedirectMarker = {
      orderToken: input.orderToken,
      redirectUrl: input.redirectUrl,
      provider: input.provider,
      timestamp: Date.now(),
    };
    this.#storedMarker.set(marker);
  }

  /**
   * Remove the marker unconditionally.
   */
  public clearMarker(): void {
    this.#storedMarker.set(null);
  }

  /**
   * Remove the marker only if it belongs to `orderToken`. Defensive against
   * the multi-tab same-order case: a marker whose token no longer matches the
   * current route is cleared without acting (edge case 6).
   */
  public clearIfMatches(orderToken: string): void {
    if (this.marker()?.orderToken === orderToken) {
      this.clearMarker();
    }
  }

  #isExpired(stored: PspRedirectMarker): boolean {
    const maxAgeMs = this.#MAX_AGE_MINUTES * 60 * 1000;
    return Date.now() - stored.timestamp > maxAgeMs;
  }
}
